import { useEffect, useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useShippingRequestStore } from 'Store/ShippingRequest'
import { useSaleOrderStore } from 'Store/SaleOrder'
import { ShippingListViewContext } from './Context'
import { ShippingFormModalContext } from '../Form/Context'
import { TableContext } from 'Context/Table'

import ActionCell from 'Presentation/Components/Table/Components/CustomActionCell'

import Swal from 'sweetalert2'
import moment from 'moment'
import 'moment-timezone'
import { shallow } from 'zustand/shallow'
import { toast } from 'react-toastify'
import { isMobile } from 'react-device-detect'

const INITIAL_FILTER = {
  saleOrderId: '',
  division: '',
  shippingDate: null,
}

const ViewModel = () => {
  const navigate = useNavigate()
  const { data, getAll, setSelectedId, deleteShipping } = useShippingRequestStore(
    state => ({
      data: state.data,
      getAll: state.getAll,
      setSelectedId: state.setSelectedId,
      deleteShipping: state.delete,
    }),
    shallow
  )
  const { saleOrder, getAllSaleOrder } = useSaleOrderStore(
    state => ({
      saleOrder: state.data,
      getAllSaleOrder: state.getAll,
    }),
    shallow
  )
  const { toggleShippingModal } = useContext(ShippingListViewContext)
  const { updateFormType, updateIsView } = useContext(ShippingFormModalContext)

  const {
    // currentSorting,
    updateLoading,
    updateError,
    updatePagination,
    updateSorting,
    resetState: resetTableState,
  } = useContext(TableContext)

  const [filter, setFilter] = useState(INITIAL_FILTER)

  const onChangeFilter = (name: string, value: any) => {
    setFilter(prev => ({ ...prev, [name]: value }))
  }

  const submitFilter = async () => {
    await fetchData()
  }

  const clearFilter = async () => {
    setFilter(INITIAL_FILTER)
    updateLoading(true)
    await getAll(INITIAL_FILTER)
    updateLoading(false)
  }

  const setupTable = () => {
    updateSorting('id', true)
    updatePagination(true)
  }

  const fetchData = async () => {
    try {
      updateLoading(true)
      await getAll({
        ...filter,
        shippingDate: !filter.shippingDate
          ? undefined
          : moment(filter.shippingDate).tz('Asia/Bangkok').toISOString(),
      })
      getAllSaleOrder({ hasProject: 1 })
      updateLoading(false)
    } catch (err: any) {
      updateLoading(false)
      updateError(err.message)
    }
  }

  const askDeleteShipping = async (id: string) => {
    const { isConfirmed } = await Swal.fire({
      title: 'คุณต้องการที่จะลบคำขอนี้ใช่หรือไม่?',
      text: 'คำขอที่ถูกลบไปแล้วจะไม่สามารถกู้คืนได้!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'ยืนยัน',
      cancelButtonText: 'ยกเลิก',
    })
    if (isConfirmed) {
      const { success, data } = await deleteShipping(parseInt(id))
      if (success) {
        toast.success('ลบคำขอการขนส่งสำเร็จ')
        fetchData()
      } else {
        toast.error(data)
      }
    }
  }

  const createShipping = () => {
    updateFormType('add')
    navigate('/shipping-request/create')
    // toggleShippingModal(true)
    updateIsView(false)
  }

  const viewShipping = (id: string) => {
    updateFormType('edit')
    toggleShippingModal(true)
    navigate(`/shipping-request/${id}?view=1`)
    setSelectedId(parseInt(id))
    updateIsView(true)
  }

  const editShipping = (id: string) => {
    updateFormType('edit')
    toggleShippingModal(true)
    navigate(`/shipping-request/${id}`)
    setSelectedId(parseInt(id))
    updateIsView(false)
  }

  const TABLE_CONFIGS: any[] = [
    {
      Header: 'ลำดับ',
      accessor: 'id',
      width: 50,
      minWidth: 50,
      Cell: ({ value }: any) => {
        return <span className='text-gray-800'>{value}</span>
      },
    },
    {
      Header: 'Sale Order',
      accessor: 'saleOrderId',
      width: 100,
      minWidth: 100,
      Cell: ({ value }: any) => {
        return (
          <span className='text-gray-800'>
            {value
              ? saleOrder.length > 0
                ? saleOrder.find(s => s.id === value)?.code
                : value
              : '-'}
          </span>
        )
      },
    },
    {
      Header: 'หน่วยงาน',
      accessor: 'division',
      width: isMobile ? 70 : 300,
      minWidth: isMobile ? 70 : 300,
      Cell: ({ value }: any) => {
        return <span className='text-gray-800'>{value}</span>
      },
    },
    {
      Header: 'สถานะ',
      accessor: 'status',
      width: 120,
      minWidth: 120,
      Cell: ({ value }: any) => {
        let badgeColor = 'bg-warning'
        if (value === 'delivering') {
          badgeColor = 'bg-warning'
        } else if (value === 'completed') {
          badgeColor = 'bg-success'
        }
        return (
          <span className={`badge ${badgeColor}`}>
            {String(value).length > 0
              ? value.substring(0, 1).toUpperCase() + value.substring(1)
              : '-'}
          </span>
        )
      },
    },
    {
      Header: 'วันที่สร้าง',
      accessor: 'createdAt',
      Cell: ({ value }: any) => {
        return <span className='text-gray-800'>{moment.utc(value).format('DD/MM/YYYY HH:mm')}</span>
      },
    },
    {
      Header: '',
      accessor: 'action',
      width: 100,
      minWidth: 100,
      Cell: ({ row }: any) => (
        <ActionCell
          id={row.original.id}
          hideDuplicate
          hideView
          onClickView={() => viewShipping(row.original.id)}
          onClickEdit={() => editShipping(row.original.id)}
          onClickDelete={() => askDeleteShipping(row.original.id)}
        />
      ),
      disableSortBy: true,
    },
  ]

  // Initial Fetch and Initial Table Setup
  useEffect(() => {
    const action = async () => {
      setupTable()
      await fetchData()
    }

    action()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Reset Hook
  useEffect(() => {
    return () => {
      resetTableState()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    data,
    TABLE_CONFIGS,
    saleOrder,
    filter,
    onChangeFilter,
    submitFilter,
    clearFilter,
    createShipping,
  }
}

export default ViewModel
